import { getNotifications } from '@api';

import {
  EMPTY_NOTIFICATIONS_FEED_SNAPSHOT,
  getNotificationSignature,
  loadLastReadAt,
  saveLastReadAt,
} from './utils';
import type { NotificationsFeedSnapshot } from './types';

export const DEFAULT_NOTIFICATIONS_POLL_INTERVAL = 15_000;

const listeners = new Set<() => void>();

let snapshot: NotificationsFeedSnapshot = EMPTY_NOTIFICATIONS_FEED_SNAPSHOT;
let organizationId: string | null = null;
let pollIntervalId: number | undefined;
let pollInterval = DEFAULT_NOTIFICATIONS_POLL_INTERVAL;
let fetching = false;

type FetchNotificationsOptions = {
  force?: boolean;
};

const emitChange = () => {
  for (const listener of listeners) {
    listener();
  }
};

const setSnapshot = (nextSnapshot: NotificationsFeedSnapshot) => {
  if (nextSnapshot === snapshot) return;

  snapshot = nextSnapshot;
  emitChange();
};

const syncPolling = () => {
  const shouldPoll = listeners.size > 0 && Boolean(organizationId);

  if (shouldPoll && pollIntervalId === undefined) {
    void fetchNotifications({ force: true });
    pollIntervalId = window.setInterval(() => {
      void fetchNotifications();
    }, pollInterval);
    return;
  }

  if (!shouldPoll && pollIntervalId !== undefined) {
    window.clearInterval(pollIntervalId);
    pollIntervalId = undefined;
  }
};

const fetchNotifications = async ({
  force = false,
}: FetchNotificationsOptions = {}) => {
  if (!organizationId || fetching) return;

  const requestOrganizationId = organizationId;
  fetching = true;

  try {
    const data = await getNotifications();
    if (requestOrganizationId !== organizationId) return;

    const latestSignature = getNotificationSignature(data.items[0]);
    const currentLatestSignature = getNotificationSignature(snapshot.items[0]);

    if (!force && latestSignature === currentLatestSignature) return;

    setSnapshot({
      items: data.items,
      total: data.total,
      status: 'ready',
      organizationId: requestOrganizationId,
      lastReadAt: loadLastReadAt(requestOrganizationId),
    });
  } catch {
    if (requestOrganizationId !== organizationId) return;

    setSnapshot({
      ...snapshot,
      organizationId: requestOrganizationId,
      status: 'error',
    });
  } finally {
    fetching = false;
  }
};

export const notificationsFeedStore = {
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    syncPolling();

    return () => {
      listeners.delete(listener);
      syncPolling();
    };
  },
  getSnapshot: () => snapshot,
  setOrganizationId: (nextOrganizationId: string | null) => {
    if (nextOrganizationId === organizationId) return;

    organizationId = nextOrganizationId;

    if (!nextOrganizationId) {
      setSnapshot(EMPTY_NOTIFICATIONS_FEED_SNAPSHOT);
      syncPolling();
      return;
    }

    setSnapshot({
      items: [],
      total: 0,
      status: 'loading',
      organizationId: nextOrganizationId,
      lastReadAt: loadLastReadAt(nextOrganizationId),
    });
    syncPolling();
  },
  refresh: () => {
    return fetchNotifications({ force: true });
  },
  markAllAsRead: () => {
    if (!organizationId) return;

    const lastReadAt = new Date().toISOString();
    saveLastReadAt(organizationId, lastReadAt);
    setSnapshot({
      ...snapshot,
      lastReadAt,
    });
  },
  setPollInterval: (interval: number) => {
    if (interval === pollInterval) return;

    pollInterval = interval;
    if (pollIntervalId === undefined) return;

    window.clearInterval(pollIntervalId);
    pollIntervalId = window.setInterval(() => {
      void fetchNotifications();
    }, pollInterval);
  },
};

export function refreshNotifications() {
  return fetchNotifications({ force: true });
}
